import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { getLocalTimezoneLabel } from '../../../utils/datetime';
import { toast } from 'react-toastify';
import { CheckIcon, XMarkIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import FormInput from '../../../components/FormInput';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import { selectBlurSensitive } from '../../../slices/preferencesSlice';
import { fetchUsers, selectUsersList } from '../../../slices/userSlice';

export default function UserProfile() {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const usersList = useSelector(selectUsersList);
  const blurSensitive = useSelector(selectBlurSensitive);

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Breadcrumbs - dynamic based on user
  const userName = user ? (user.username || user.email || 'User Profile') : 'User Profile';
  useBreadcrumbs([
    { label: 'Users', href: '/users/list-users' },
    { label: userName }
  ], "View this user's account details");

  useEffect(() => {
    // If users list is empty, fetch it
    if (!usersList || usersList.length === 0) {
      dispatch(fetchUsers());
    }
  }, [dispatch, usersList]);

  useEffect(() => {
    // Find the user in the list
    if (usersList && usersList.length > 0) {
      const foundUser = usersList.find(u => String(u.id) === String(userId));
      if (foundUser) {
        setUser(foundUser);
        setLoading(false);
      } else {
        setLoading(false);
        toast.error('User not found');
      }
    }
  }, [userId, usersList]);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    }).catch(() => {
      toast.error('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-gray-200 border-t-brand-600 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12 text-gray-500">User not found</div>
      </div>
    );
  }

  const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email;

  return (
    <div className="space-y-6">
      {/* 2-Column Layout */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 items-stretch">
        {/* Left Column: User Summary */}
        <div className="col-span-1 rounded-lg bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70 h-full flex flex-col">
          <div className="flex flex-col gap-4 h-full">
            {/* User Info */}
            <div>
              <div className={`text-lg font-semibold text-gray-900 dark:text-gray-100 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>
                {displayName}
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                @{user.username || '—'}
              </div>
              <div className={`text-sm text-gray-600 dark:text-gray-400 mt-1 truncate break-all ${blurSensitive ? 'blur-sm select-none' : ''}`}>
                {user.email || '—'}
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2">
              {user.is_verified && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-800 dark:text-green-100">
                  <CheckIcon className="h-3.5 w-3.5" />
                  Verified
                </span>
              )}
              {!user.is_verified && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  <XMarkIcon className="h-3.5 w-3.5" />
                  Not Verified
                </span>
              )}
              {user.is_superuser && (
                <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-200">
                  Admin
                </span>
              )}
            </div>

            {/* Account Details */}
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">User ID</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{user.id}</span>
                  <button
                    onClick={() => copyToClipboard(user.id, 'User ID')}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Copy User ID"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Date Joined</div>
                <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                  {user.date_joined ? moment(user.date_joined).format('MMMM D, YYYY') : '—'}
                </div>
              </div>

              {user.last_login && (
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Last Seen</div>
                  <div className="mt-1 font-medium text-gray-900 dark:text-gray-100">
                    {moment(user.last_login).fromNow()}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {moment(user.last_login).format('MMM D, YYYY [at] h:mm A')}{' '}
                    {getLocalTimezoneLabel(user.last_login)}
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Right Column: Details & Sections */}
        <div className="col-span-1 lg:col-span-2 space-y-6 h-full">
          {/* Profile Information Card */}
          <div className="rounded-lg bg-surface dark:bg-gray-870 shadow-sm border border-gray-200/70 dark:border-gray-600/70 h-full flex flex-col">
            {/* Section 1: Basic Info (read-only) */}
            <div className="p-6 flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Basic Information
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      First Name
                    </label>
                    <FormInput
                      name="first_name"
                      value={user.first_name || ''}
                      readOnly={true}
                      className={blurSensitive ? 'blur-sm select-none pointer-events-none' : ''}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Last Name
                    </label>
                    <FormInput
                      name="last_name"
                      value={user.last_name || ''}
                      readOnly={true}
                      className={blurSensitive ? 'blur-sm select-none pointer-events-none' : ''}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Username
                  </label>
                  <FormInput
                    name="username"
                    value={user.username || ''}
                    readOnly={true}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email Address
                  </label>
                  <FormInput
                    name="email"
                    type="email"
                    value={user.email || ''}
                    readOnly={true}
                    className={blurSensitive ? 'blur-sm select-none pointer-events-none' : ''}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Managed Resources */}
            {user.managed_resources && user.managed_resources.length > 0 && (
              <div className="px-6 pb-6 mt-4">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Managed Resources
                </h3>
                <div className="space-y-3">
                  {user.managed_resources.map((resource) => (
                    <div
                      key={resource.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {resource.name}
                          </span>
                          <span className="inline-flex items-center rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-200 capitalize shrink-0">
                            {resource.role}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-2">
                          <span>
                            ID: {resource.id}
                          </span>
                          <button
                            onClick={() => copyToClipboard(resource.id, 'Resource ID')}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            title="Copy Resource ID"
                          >
                            <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Access granted {moment(resource.created_at).fromNow()}
                          <span className="mx-1">•</span>
                          {moment(resource.created_at).format('MMM D, YYYY')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 3: Notification Preferences */}
            {user.notification_preferences && (
              <div className="px-6 pb-6 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    Notification Preferences
                  </h3>
                  {user.notification_preferences.updated_at && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Updated {moment(user.notification_preferences.updated_at).fromNow()}
                    </span>
                  )}
                </div>
                <div className="space-y-4">
                  {/* Alert Emails */}
                  <div className="flex items-start justify-between py-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Alert Emails
                        </span>
                        {user.notification_preferences.enable_alert_emails ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
                            <CheckIcon className="h-3 w-3" />
                            On
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                            <XMarkIcon className="h-3 w-3" />
                            Off
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Export notifications and system alerts
                      </p>
                      {user.notification_preferences.alerts_email && (
                        <div className={`mt-2 inline-flex items-center gap-1.5 ${blurSensitive ? 'blur-sm select-none pointer-events-none' : ''}`}>
                          <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                          </svg>
                          <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                            {user.notification_preferences.alerts_email}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Invoice Emails */}
                  <div className="flex items-start justify-between py-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Invoice Emails
                        </span>
                        {user.notification_preferences.enable_invoice_emails ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
                            <CheckIcon className="h-3 w-3" />
                            On
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                            <XMarkIcon className="h-3 w-3" />
                            Off
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Payment and billing notifications
                      </p>
                      {user.notification_preferences.invoice_email && (
                        <div className={`mt-2 inline-flex items-center gap-1.5 ${blurSensitive ? 'blur-sm select-none pointer-events-none' : ''}`}>
                          <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                          </svg>
                          <span className="text-xs font-mono text-gray-700 dark:text-gray-300">
                            {user.notification_preferences.invoice_email}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Forecast Submission Alerts */}
                  <div className="flex items-start justify-between py-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Forecast Submission Alerts
                        </span>
                        {user.notification_preferences.enable_forecast_submission_alerts ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/40 dark:text-green-400">
                            <CheckIcon className="h-3 w-3" />
                            On
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                            <XMarkIcon className="h-3 w-3" />
                            Off
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Notifications about forecast submissions and deadlines
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 4: Role & Permissions */}
            {/* Role & Permissions disabled
            <div className="p-6">
              <div className="text-sm text-gray-500 dark:text-gray-400 p-3 bg-surface dark:bg-gray-870 rounded-lg">
                Role & Permissions section is currently disabled.
              </div>
            </div>
            */}
           </div>
         </div>
       </div>
     </div>
   );
 }
